import React, { useState, useEffect, useRef } from 'react';
import { Modal, Space, Typography, Button, Tooltip, message, Alert } from 'antd';
import { CopyOutlined, CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { PatCreateResponse } from '@/services/identity/pat.service';
import { logEvent, logError } from '@/appInsights';

const TOKEN_CLEAR_DELAY_MS = 15 * 60 * 1000;

export interface PersonalAccessTokenDisplayModalProps {
  visible: boolean;
  token: PatCreateResponse | null;
  onClose: () => void;
}

export const PersonalAccessTokenDisplayModal: React.FC<PersonalAccessTokenDisplayModalProps> = ({
  visible,
  token,
  onClose,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const clearTokenTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (visible && token) {
      clearTokenTimeoutRef.current = setTimeout(() => {
        onClose();
        message.warning({
          content:
            t('common.patTokenExpiredWarning') ||
            'Token display has expired for security. Please create a new one if needed.',
        });
      }, TOKEN_CLEAR_DELAY_MS);
    }

    return () => {
      if (clearTokenTimeoutRef.current) {
        clearTimeout(clearTokenTimeoutRef.current);
        clearTokenTimeoutRef.current = null;
      }
    };
  }, [visible, token, onClose, t]);

  const handleCopyToken = async () => {
    if (!token) return;

    try {
      await navigator.clipboard.writeText(token.token);
      setCopied(true);
      logEvent('PAT_Token_Copied', { tokenName: token.name });
      message.success(t('common.patTokenCopied') || 'Token copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      const errorMessage = 'Failed to copy token to clipboard';
      logError(errorMessage, { action: 'copy_token' });
      message.error(t('common.patTokenCopyFailed') || errorMessage);
    }
  };

  const handleConfirmSaved = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Modal
      title={t('common.patTokenCreatedTitle') || 'Token Created'}
      open={visible && !!token}
      onCancel={handleConfirmSaved}
      footer={
        <Space style={{ width: '100%', justifyContent: 'flex-end', display: 'flex' }}>
          <Button type='primary' onClick={handleConfirmSaved}>
            {t('common.patConfirmSaved') || 'I have saved the PAT'}
          </Button>
        </Space>
      }
      closable={false}
    >
      <Space direction='vertical' style={{ width: '100%' }} size='large'>
        <Alert
          message={
            t('common.patTokenWarning') ||
            'This token will only be displayed once. Store it securely and never share it with others.'
          }
          type='warning'
          showIcon
        />

        {token?.expiresAt && (
          <Space direction='vertical' style={{ width: '100%' }} size='small'>
            <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
              {t('common.patExpiresAt') || 'Expires At'}
            </Typography.Text>
            <Typography.Paragraph style={{ margin: 0 }}>
              {new Date(token.expiresAt).toLocaleDateString()}
            </Typography.Paragraph>
          </Space>
        )}

        <div style={{ backgroundColor: '#f5f5f5', borderRadius: '6px', padding: '16px' }}>
          <Space direction='vertical' style={{ width: '100%' }} size='small'>
            <Typography.Text type='secondary' style={{ fontSize: '12px' }}>
              {t('common.patTokenValue') || 'Token Value'}
            </Typography.Text>
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Typography.Paragraph
                style={{
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  wordBreak: 'break-all',
                  margin: 0,
                  flex: 1,
                }}
              >
                {token?.token.substring(0, 20)}...
              </Typography.Paragraph>
              <Tooltip title={copied ? 'Copied!' : 'Copy token'}>
                <Button
                  type='default'
                  icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                  onClick={handleCopyToken}
                />
              </Tooltip>
            </Space>
          </Space>
        </div>
      </Space>
    </Modal>
  );
};
